/*******************************************************************************
 * Copyright (c) 2025 Bula Technologies
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 1.0.0
 *
 *******************************************************************************/

import { useEffect, useState } from "react";
import { Box, Grid, Typography, CircularProgress, TableRow, TableHead, Table, TableCell, TableBody, Button, TextField, TablePagination } from "@mui/material";
import { useTheme } from "@emotion/react";
import BackButton from "../../../../components/shared/Buttons/BackButton";
import { callExportLedgerToCSV, callGetLedgerRecords } from "../../../../services/ledger/LedgerController";
import { formatFirestoreTimestamp } from "../../../../utils/formatTimestampToDate";
import DownloadIcon from "@mui/icons-material/Download";
import { SearchSharp } from "@mui/icons-material";
import { PaymentDetailsPreview } from "./views/PaymentDetailsPreview";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const ViewLedgerPage = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [pageTokens, setPageTokens] = useState([]);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [exportLoading, setExportLoading] = useState(false);

    const theme = useTheme();

    //  Fetch ledger records
    const handleFetch = async (cursor = null, limitOverride = rowsPerPage) => {
        setLoading(true);
        try {
            if (startDate !== "" && endDate !== "") {
                const result = await callGetLedgerRecords({
                    startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                    endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
                    lastDocId: cursor,
                    limit: limitOverride,
                });

                setRecords(result.records || []);
                setNextPageToken(result.nextPageToken || null);
                setTotalCount(result.totalCount || 0);
                setTotalPages(result.totalPages || 0);
            }

        } catch (err) {
            console.error("Error fetching ledger:", err);
        } finally {
            setLoading(false);
        }
    };

    //  Handle filtering
    const handleSearch = () => {
        if (!startDate || !endDate) return;
        setPage(0);
        setPageTokens([]);
        setNextPageToken(null);
        handleFetch();
    };

    //  Handle next / previous page
    const handleChangePage = async (event, newPage) => {
        setLoading(true);
        try {
            if (newPage > page) {
                // Next page
                if (nextPageToken) {
                    const currentToken = nextPageToken;

                    const result = await callGetLedgerRecords({
                        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
                        lastDocId: currentToken,
                        limit: rowsPerPage,
                    });
                    setRecords(result.records || []);
                    setNextPageToken(result.nextPageToken || null);
                    setPageTokens((prev) => [...prev, currentToken]);
                    setTotalCount(result.totalCount || 0);
                    setTotalPages(result.totalPages || 0);
                }
            } else {
                // Previous page
                const prevCursor = pageTokens[newPage - 1] || null;
                const result = await callGetLedgerRecords({
                    startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                    endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
                    lastDocId: prevCursor,
                    limit: rowsPerPage,
                });
                setRecords(result.records || []);
                setNextPageToken(result.nextPageToken || null);
                setTotalCount(result.totalCount || 0);
                setTotalPages(result.totalPages || 0);
            }

            setPage(newPage);
        } catch (err) {
            console.error("Pagination error:", err);
        } finally {
            setLoading(false);
        }
    };

    //  Handle page size change
    const handleChangeRowsPerPage = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setRowsPerPage(newSize);
        setPage(0);
        setPageTokens([]);
        setNextPageToken(null);

        handleFetch(null, newSize);
    };

    useEffect(() => {
        handleFetch();
    }, []);


    const handleExport = async (startDate, endDate) => {
        try {
            if (!startDate || !endDate) {
                alert("Please select both start and end dates.");
                return;
            }
            setExportLoading(true);

            const result = await callExportLedgerToCSV({
                startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
            });
            const { fileName, fileContent } = result;

            // Convert Base64 → Blob → Download
            const blob = new Blob(
                [Uint8Array.from(atob(fileContent), (c) => c.charCodeAt(0))],
                { type: "text/csv;charset=utf-8;" }
            );

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Export error:", err);
            alert("Failed to export ledger.");
        } finally {
            setExportLoading(false);
        }
    };
    const capitalizeFirst = (str) =>
        typeof str === "string" && str.length > 0
            ? str.charAt(0).toUpperCase() + str.slice(1)
            : str;


    return (
        <Box m={3}>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <BackButton pageTitle="View Ledger" />
                </Grid>

                {/*  Date Filters */}
                <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>

                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={!!startDate && endDate && dayjs(startDate).isAfter(endDate)}
                                        helperText={
                                            startDate && endDate && dayjs(startDate).isAfter(endDate)
                                                ? "Start date cannot be after End date"
                                                : ""
                                        }
                                    />
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#fff", // default border color
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#fff", // hover border color
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#fff", // focused border color
                                        },
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#fff !important", // force white border always
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "#000", // text color
                                    },
                                }}
                                maxDate={dayjs()}
                            />

                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={!!startDate && endDate && dayjs(startDate).isAfter(endDate)}
                                        helperText={
                                            startDate && endDate && dayjs(startDate).isAfter(endDate)
                                                ? "End date cannot be before Start date"
                                                : ""
                                        }
                                    />
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#fff", // default border color
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#fff", // hover border color
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#fff", // focused border color
                                        },
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#fff !important", // force white border always
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "#000", // text color
                                    },
                                }}
                                maxDate={dayjs()}
                            />

                            <Box display="flex" gap={2} flexWrap="wrap">
                                {/* 🔹 Filter Button — uses theme primary color */}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SearchSharp />}
                                    onClick={handleSearch}
                                    disabled={loading}
                                    sx={{
                                        height: 56,
                                        px: 3,
                                        fontWeight: 600,
                                        textTransform: "none",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Filter
                                </Button>

                                {/*   Export Button — uses Bula gradient */}
                                <Button
                                    variant="contained"
                                    startIcon={exportLoading ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        <DownloadIcon />
                                    )}
                                    disabled={exportLoading}

                                    onClick={() => handleExport(startDate, endDate)}
                                    sx={{
                                        height: 56,
                                        px: 3,
                                        fontWeight: 600,
                                        textTransform: "none",
                                        whiteSpace: "nowrap",
                                        background: `linear-gradient(90deg, ${theme.bulaYellow} 0%, ${theme.bulaOrange} 100%)`,
                                        color: "#fff",
                                        boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
                                        "&:hover": {
                                            background: `linear-gradient(90deg, ${theme.bulaOrange} 0%, ${theme.bulaYellow} 100%)`,
                                        },
                                    }}
                                >
                                    Export to CSV
                                </Button>
                            </Box>
                        </Box>
                    </LocalizationProvider>
                </Grid>


                {/*  Ledger Table */}
                <Grid item xs={12}>
                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="40vh"
                        >
                            <CircularProgress />
                        </Box>
                    ) : records.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>User ID</TableCell>
                                        <TableCell>Ledger ID</TableCell>

                                        <TableCell>Type</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Currency</TableCell>
                                        <TableCell>Payment Status</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Release Reason</TableCell>

                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {records.map((r) => (
                                        <TableRow key={r.id}>
                                            <TableCell>
                                                {r.createdDate
                                                    ? formatFirestoreTimestamp(r.createdDate)
                                                    : "-"}
                                            </TableCell>

                                            <TableCell>{r.userId ?? ''}</TableCell>
                                            <TableCell>{r.ledgerId ?? ''}</TableCell>

                                            <TableCell>{r.type ?? ''}</TableCell>
                                            <TableCell>
                                                {r.amount?.toLocaleString("en-US", {
                                                    style: "currency",
                                                    currency: r.currency || "USD",
                                                })}
                                            </TableCell>
                                            <TableCell>{r.currency}</TableCell>
                                            <TableCell>
                                                {r.paymentDetails?.length ? (
                                                    <PaymentDetailsPreview
                                                        details={r.paymentDetails}
                                                        paymentStatus={r.paymentStatus}
                                                        merchantTransactionID={r.merchantTransactionID}

                                                    />
                                                ) : (
                                                    capitalizeFirst(r.paymentStatus ?? '')
                                                )}
                                            </TableCell>

                                            <TableCell>{r.description || "-"}</TableCell>
                                            <TableCell>{r.releaseReason || "-"}</TableCell>


                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {/*  Pagination Controls */}
                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelDisplayedRows={({ page }) =>
                                    `Page ${page + 1} of ${totalPages}`
                                }
                                rowsPerPageOptions={[20, 50, 100]}
                                nextIconButtonProps={{
                                    disabled: page + 1 >= totalPages || !nextPageToken,
                                }}
                                backIconButtonProps={{ disabled: page === 0 }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            No ledger records available.
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewLedgerPage;